import React, {OptionHTMLAttributes, useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import * as Yup from 'yup';
import {Alert, Form, Row, Table} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {FormikProps, useFormik} from 'formik';
import toast from 'react-hot-toast';
import {BillReclamationForm, postBillReclamation} from '../Api/forms';
import SelectBill from './SelectBill';
import TextAreaInputControlled from '../../../app/modules/form/TextAreaInputControlled';
import ButtonSubmit from '../../../app/modules/form/ButtonSubmit';
import {useMutation, useQueryClient} from 'react-query';
import SelectReason, {getReasonOptions} from './SelectReason';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {ReclamationReason, ReclamationTypes} from '../Type/reclamation';
import ButtonReset from '../../../app/modules/form/ButtonReset';
import {useGetMe} from '../../../app/hooks/useGetMe';
import FileInputControlled from '../../../app/modules/form/FileInputControlled';
import SelectType, {getTypeOptions} from './SelectType';
import SelectSite from '../../Auth/Components/SelectSite';
import {usePermissions} from '../../../app/modules/auth/authorization/PermissionProvider';
import {useTemplateManager} from '../Helpers/TemplateManager';
import {useGetContactInfo} from '../../Service/Hooks/ContactManager';
import SelectService from '../../Auth/Components/SelectService';
import SelectBu from '../../Auth/Components/SelectBu';
import styles from './Reclamation.module.scss';
import clsx from 'clsx';
import {SetFieldValue} from '../../../app/types/Forms';

type ReclamationNavigationState = {
  reclamationData?: {
    type: ReclamationTypes;
    motif?: ReclamationReason;
    objet?: string;
    serviceId?: number;
    serviceUid?: string;
  };
};

const MESSAGE_MAX_LENGTH = 500;
const SUBJECT_MAX_LENGTH = 100;

const Reclamation = (): JSX.Element => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const customer = useGetMe();
  const requiredFieldMessage = intl.formatMessage({id: 'FIELD.REQUIRED'});
  const reasonOptions = getReasonOptions(intl, ReclamationTypes.ADMINISTRATIVE);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {mutate, isLoading} = useMutation({
    mutationFn: postBillReclamation,
    onSuccess: (data) => {
      setErrorMessage(null);
      const ticketNumber = data?.TicketNumber;
      toast.success(
        ticketNumber
          ? intl.formatMessage(
              {id: 'ADMINISTRATIVE.RECLAMATIONS.FORM.SUCCESS.WITH_NUMBER'},
              {number: ticketNumber}
            )
          : intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.FORM.SUCCESS'})
      );
      queryClient.invalidateQueries(['tickets']);
      navigate('/support-list');
    },
    onError: (error: unknown) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const messageId =
        status && status >= 500
          ? 'ADMINISTRATIVE.RECLAMATIONS.FORM.ERROR.SERVER'
          : 'ADMINISTRATIVE.RECLAMATIONS.FORM.ERROR';
      const message = intl.formatMessage({id: messageId});
      setErrorMessage(message);
      toast.error(message);
    },
  });

  const defaultBillNumber = searchParams.get('bill');
  const orderId = searchParams.get('orderId');
  const routerId = searchParams.get('routerId');
  const defaultSite = searchParams.get('site');
  const defaultService = searchParams.get('service');

  const CISID = searchParams.get('ServiceId');
  const TemplateReclamation = searchParams.get('TemplateReclamation');
  const {getTempleteById, dataService} = useTemplateManager(CISID ? CISID : '');
  const {data: dataContact} = useGetContactInfo();

  let selectedReason = defaultBillNumber
    ? reasonOptions.find((reason) => reason.value === ReclamationReason.BILL)
    : reasonOptions.find((reason) => reason.value === ReclamationReason.LIVRAISON);

  const defaultType = useMemo(() => {
    if (routerId) {
      return {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.TYPE.TECHNICAL'}),
        value: ReclamationTypes.TECHNICAL,
      };
    }
    if (orderId || defaultBillNumber) {
      return {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.TYPE.ADMINISTRATIVE'}),
        value: ReclamationTypes.ADMINISTRATIVE,
      };
    }
    return undefined;
  }, [orderId, defaultBillNumber, routerId, intl]);

  const location = useLocation() as {
    state?: ReclamationNavigationState;
  };
  const reclamationData = location.state?.reclamationData;
  const initialValues: BillReclamationForm = {
    type: defaultType,
    reason: defaultBillNumber
      ? reasonOptions.find((reason) => reason.value === ReclamationReason.BILL)
      : orderId
      ? selectedReason
      : undefined,
    bill: undefined,
    subject: '',
    message: '',
    service: undefined,
    customer: undefined,
  };

  const InitialFormTouched = {
    type: false,
    reason: false,
    bill: false,
    subject: false,
    message: false,
    service: false,
    customer: false,
  };

  const validationRules = Yup.object().shape({
    type: Yup.object().required(requiredFieldMessage),
    reason: Yup.object().required(requiredFieldMessage),
    bill: Yup.object().when('reason', {
      is: (reason: OptionHTMLAttributes<string> | undefined) =>
        reason?.value === ReclamationReason.BILL,
      then: Yup.object().nullable().required(requiredFieldMessage),
    }),
    subject: Yup.string()
      .max(SUBJECT_MAX_LENGTH, intl.formatMessage({id: 'TOO.LONG'}, {max: SUBJECT_MAX_LENGTH}))
      .required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
    message: Yup.string()
      .max(MESSAGE_MAX_LENGTH, intl.formatMessage({id: 'TOO.LONG'}, {max: MESSAGE_MAX_LENGTH}))
      .required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
    service: Yup.object().nullable(),
  });

  const prefilledValues = useMemo<BillReclamationForm | null>(() => {
    if (!reclamationData) return null;
    const typeOption = getTypeOptions(intl).find((t) => t.value === reclamationData.type);

    if (!typeOption) return null;
    const reasonOption = reclamationData.motif
      ? getReasonOptions(intl, reclamationData.type).find((r) => r.value === reclamationData.motif)
      : undefined;

    return {
      type: typeOption,
      reason: reasonOption,
      bill: undefined,
      subject: reclamationData.objet ?? '',
      message: '',
      service: undefined,
      customer: undefined,
    };
  }, [reclamationData, intl]);

  const formik: FormikProps<BillReclamationForm> = useFormik({
    initialValues: prefilledValues ?? initialValues,
    initialTouched: InitialFormTouched,
    validationSchema: validationRules,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: async (values: BillReclamationForm) => {
      const selectedCustomerId = values.customer?.id ?? customer.id;
      mutate({
        ...values,
        clientId: selectedCustomerId.toString(),
        customerId: selectedCustomerId,
      });
    },
  });

  useEffect(() => {
    if (orderId) {
      formik.setFieldValue(
        'message',
        intl.formatMessage({id: 'TICKET.RECLAMATION.DOSSIER'}) + orderId + '\n'
      );
      formik.setFieldValue('reason', selectedReason);
    }

    if (defaultBillNumber) {
      formik.setFieldValue('reason', selectedReason);
    }

    if (routerId) {
      // eslint-disable-next-line
      selectedReason = getReasonOptions(intl, ReclamationTypes.TECHNICAL).find(
        (reason) => reason.value === ReclamationReason.REQUEST_FOR_CONFIGURATION
      );
      formik.setFieldValue('reason', selectedReason);
    }

    if (TemplateReclamation) {
      let dataToInject: any = {};
      if (searchParams.get('music') != null) dataToInject['music'] = searchParams.get('music');
      if (searchParams.get('msisdn') != null) dataToInject['msisdn'] = searchParams.get('msisdn');
      if (searchParams.get('ficheId') != null)
        dataToInject['ficheId'] = searchParams.get('ficheId');
      if (dataContact) {
        dataToInject = {...dataToInject, ...dataContact};
      }

      const template = getTempleteById(TemplateReclamation, dataToInject);

      if (template) {
        if (template.type) {
          formik.setFieldValue(
            'type',
            getTypeOptions(intl).find((type) => type.value === template.type)
          );
        }

        if (template.reason) {
          setTimeout(() => {
            formik.setFieldValue(
              'reason',
              getReasonOptions(intl, ReclamationTypes.TECHNICAL).find(
                (reason) => reason.value === template.reason
              )
            );
          }, 1000);
        }

        if (template.content) {
          formik.setFieldValue('message', template.content);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataService, dataContact]);

  const handleFileChange = (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      formik.setFieldValue('file', e.target.files[0]);
    }
  };

  const userPermissions = usePermissions();

  const canSeeTechnical = !!userPermissions?.includes?.('voirTicketTechnique');
  const canSeeAdmin = !!userPermissions?.includes?.('voirTicketAdministratif');

  const onlyTech = canSeeTechnical && !canSeeAdmin;
  const onlyAdmin = canSeeAdmin && !canSeeTechnical;

  useEffect(() => {
    if (onlyTech) {
      formik.setFieldValue('type', {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.TYPE.TECHNICAL'}),
        value: ReclamationTypes.TECHNICAL,
      });
    }

    if (onlyAdmin) {
      formik.setFieldValue('type', {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.TYPE.ADMINISTRATIVE'}),
        value: ReclamationTypes.ADMINISTRATIVE,
      });
    }
    // eslint-disable-next-line
  }, []);

  const showBillCombo = [
    ReclamationReason.BILL,
    ReclamationReason.DOUBLE_FACTU,
    ReclamationReason.ERROR_FACTU,
    ReclamationReason.RESIL_FACTU,
  ].includes(formik.values.reason?.value as ReclamationReason);

  const hasType = typeof formik.values.type?.value !== 'undefined';
  const hasReason = typeof formik.values.reason?.value !== 'undefined';

  const setTypeAndReset = useCallback<SetFieldValue>((field, value) => {
    formik.setFieldValue(field, value);
    if (field === 'type') {
      formik.setFieldValue('reason', undefined);
      formik.setFieldValue('bill', undefined);
      formik.setFieldValue('site', undefined);
      formik.setFieldValue('service', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setReasonAndReset = useCallback<SetFieldValue>((field, value) => {
    formik.setFieldValue(field, value);
    if (field === 'reason') {
      formik.setFieldValue('bill', undefined);
      formik.setFieldValue('site', undefined);
      formik.setFieldValue('service', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSiteAndService = [
    ReclamationReason.ERREUR_RESIL,
    ReclamationReason.CONTEST_ABO,
    ReclamationReason.DEGRADATION_OF_SERVICE,
    ReclamationReason.SERVICE_OUTAGE,
    ReclamationReason.REQUEST_FOR_INFORMATION,
    ReclamationReason.REQUEST_FOR_CONFIGURATION,
  ].includes(formik.values.reason?.value as ReclamationReason);

  // BU portée par le service sélectionné, pour pré-remplir le sélecteur de BU.
  const selectedServiceBuId = formik.values.service?.data?.buId ?? formik.values.service?.buId;

  return (
    <div className={clsx(styles.reclamationContainer)}>
      <Row>
        <SelectType
          name="type"
          value={formik.values.type}
          setFieldValue={setTypeAndReset}
          errors={formik.errors.type}
        />
      </Row>
      {hasType && (
        <Row className={'mt-3'}>
          <SelectReason
            type={
              formik.values.type?.value
                ? formik.values.type?.value.toString()
                : ReclamationTypes.ADMINISTRATIVE
            }
            name={'reason'}
            value={formik.values.reason}
            setFieldValue={setReasonAndReset}
            isLoading={isLoading}
            isDisabled={typeof formik.values.type?.value === 'undefined'}
            touched={formik.touched.reason}
            error={formik.errors.reason}
          />
        </Row>
      )}
      {formik.values.reason?.value === ReclamationReason.DOC_DUPLICATA && (
        <Row className={'mt-3'}>
          <Alert variant="info" className="mb-0 mx-3 flex-shrink-1">
            <Alert.Heading className="fs-6 fw-bold">
              {intl.formatMessage({
                id: 'ADMINISTRATIVE.RECLAMATIONS.DOC_DUPLICATA.ALERT.TITLE',
              })}
            </Alert.Heading>
            <p className="mb-2 fst-italic">
              {intl.formatMessage({
                id: 'ADMINISTRATIVE.RECLAMATIONS.DOC_DUPLICATA.ALERT.SUBTITLE',
              })}
            </p>
            <Table size="sm" borderless className="mb-0 w-100 w-md-50">
              <thead>
                <tr>
                  <th>
                    {intl.formatMessage({
                      id: 'ADMINISTRATIVE.RECLAMATIONS.DOC_DUPLICATA.ALERT.HEADER.QUANTITY',
                    })}
                  </th>
                  <th>
                    {intl.formatMessage({
                      id: 'ADMINISTRATIVE.RECLAMATIONS.DOC_DUPLICATA.ALERT.HEADER.PRICE',
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {['TIER1', 'TIER2', 'TIER3', 'TIER4', 'TIER5'].map((tier) => {
                  const text = intl.formatMessage({
                    id: `ADMINISTRATIVE.RECLAMATIONS.DOC_DUPLICATA.ALERT.${tier}`,
                  });
                  const separatorIndex = text.lastIndexOf(':');
                  const quantity =
                    separatorIndex >= 0 ? text.slice(0, separatorIndex).trim() : text;
                  const price = separatorIndex >= 0 ? text.slice(separatorIndex + 1).trim() : '';
                  return (
                    <tr key={tier}>
                      <td>{quantity}</td>
                      <td>{price}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Alert>
        </Row>
      )}
      {hasReason && (
        <>
          {!onlyTech && showBillCombo && (
            <Row className={'mt-3'}>
              <SelectBill
                name="bill"
                value={formik.values.bill}
                setFieldValue={formik.setFieldValue}
                touched={formik.touched.bill}
                error={formik.errors.bill as string}
                defaultBillNumber={defaultBillNumber || undefined}
              />
            </Row>
          )}

          {(!(!onlyTech && showBillCombo) || !!formik.values.bill) && (
            <>
              {showSiteAndService && (
                <>
                  <Row className={'mt-3'}>
                    <SelectSite
                      defaultValue={defaultSite}
                      name={'site'}
                      value={formik.values.site}
                      setFieldValue={formik.setFieldValue}
                      isDisabled={typeof formik.values.type?.value === 'undefined'}
                      touched={formik.touched.site}
                      error={formik.errors.site}
                    />
                  </Row>

                  <Row className={'mt-3'}>
                    <SelectService
                      name="service"
                      value={formik.values.service}
                      setFieldValue={formik.setFieldValue}
                      isDisabled={typeof formik.values.type?.value === 'undefined'}
                      touched={formik.touched.service}
                      error={formik.errors.service}
                      selectedSites={formik.values.site}
                      defaultValue={defaultService || undefined}
                    />
                  </Row>
                </>
              )}

              <Row className={'mt-3'}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-3 form-label required">
                    {intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.SUBJECT'})}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.subject && !!formik.errors.subject}
                    maxLength={100}
                    placeholder={intl.formatMessage({
                      id: 'ADMINISTRATIVE.RECLAMATIONS.SUBJECT.PLACEHOLDER',
                    })}
                  />
                  {formik.touched.subject && formik.errors.subject && (
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.subject}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Row>

              <Row className={'mt-3'}>
                <TextAreaInputControlled
                  name="message"
                  label={intl.formatMessage({id: 'CONTACT.FORM.MESSAGE'})}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onReset={formik.handleReset}
                  errors={formik.errors.message}
                  touched={formik.touched.message}
                  value={formik.values.message}
                  maxLength={MESSAGE_MAX_LENGTH}
                />
              </Row>

              <Row className={'mt-3'}>
                <Form.Group>
                  <FileInputControlled
                    name="file"
                    value={formik.values.file}
                    label={intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.ATTACHMENT'})}
                    placeholder={intl.formatMessage({id: 'INPUT.FILE.PLACEHOLDER'})}
                    onChange={handleFileChange}
                    onBlur={formik.handleBlur}
                    onReset={formik.handleReset}
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp"
                    errors={formik.errors.file as string}
                    touched={formik.touched.file}
                  />
                </Form.Group>
              </Row>

              <SelectBu
                name="customer"
                value={formik.values.customer}
                setFieldValue={formik.setFieldValue}
                serviceBuId={selectedServiceBuId}
              />

              {errorMessage && (
                <Row className={'mt-3'}>
                  <Alert variant="danger" className="mb-0 mx-3">
                    {errorMessage}
                  </Alert>
                </Row>
              )}

              <Form.Group className="justify-content-end d-flex gap-2 gap-lg-3 mt-4">
                <ButtonReset
                  onClick={() => formik.resetForm()}
                  textId={'ADMINISTRATIVE.RECLAMATIONS.CANCEL'}
                  size={'small'}
                />
                <ButtonSubmit
                  disabled={formik.isSubmitting}
                  isLoading={isLoading}
                  textId={'ADMINISTRATIVE.RECLAMATIONS.SEND'}
                  size={'small'}
                  onClick={formik.submitForm}
                />
              </Form.Group>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Reclamation;
